import { Components } from '@mui/material';
import type {} from '@mui/lab/themeAugmentation';
import { alpha, Theme } from '@mui/material/styles';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
  }
}

type ButtonVisualState = {
  backgroundColor: string;
  borderColor: string;
  color: string;
  iconColor?: string;
  border?: string;
  boxShadow?: string;
  outline?: string;
  outlineOffset?: string;
  opacity?: number;
};

type ButtonStateStyles = {
  default: ButtonVisualState;
  hover: ButtonVisualState;
  focus: ButtonVisualState;
  active: ButtonVisualState;
  disabled: ButtonVisualState;
};

type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

const getInsetBorder = (color: string) => `inset 0 0 0 1px ${color}`;

const getTertiaryButtonStates = (
  theme: Theme,
  inverted = false,
): ButtonStateStyles => {
  const color = inverted ? theme.palette.text.light : theme.palette.text.subtle;

  const defaultState: ButtonVisualState = {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    color,
    iconColor: color,
    border: '0',
  };

  return {
    default: defaultState,
    hover: {
      ...defaultState,
      backgroundColor: alpha(
        theme.palette.background.tertiaryHover,
        Number.parseFloat(theme.lelyOpacity.MD) / 100,
      ),
    },
    focus: {
      ...defaultState,
      borderColor: color,
      boxShadow: 'none',
      outline: `${theme.lelySpacing._2XS} solid ${color}`,
      outlineOffset: theme.lelySpacing._2XS,
    },
    active: {
      ...defaultState,
      backgroundColor: alpha(
        theme.palette.background.tertiaryActive,
        Number.parseFloat(theme.lelyOpacity.LG) / 100,
      ),
    },
    disabled: {
      ...defaultState,
      opacity: Number.parseFloat(theme.lelyOpacity.LG) / 100,
    },
  };
};

const getButtonStates = (
  theme: Theme,
): Record<ButtonVariant, ButtonStateStyles> => ({
  primary: {
    default: {
      backgroundColor: theme.palette.background.primaryDefault,
      borderColor: theme.palette.background.primaryDefault,
      color: theme.palette.text.light,
    },
    hover: {
      backgroundColor: theme.palette.background.primaryHover,
      borderColor: theme.palette.background.primaryHover,
      color: theme.palette.text.light,
    },
    focus: {
      backgroundColor: theme.palette.background.primaryDefault,
      borderColor: theme.palette.border.focus,
      color: theme.palette.text.light,
      boxShadow: 'none',
      outline: `${theme.lelySpacing._2XS} solid ${theme.palette.border.focus}`,
      outlineOffset: theme.lelySpacing._2XS,
    },
    active: {
      backgroundColor: theme.palette.background.primaryActive,
      borderColor: theme.palette.background.primaryActive,
      color: theme.palette.text.light,
    },
    disabled: {
      backgroundColor: theme.palette.action.disabledBackground,
      borderColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  secondary: {
    default: {
      backgroundColor: theme.palette.background.secondaryDefault,
      borderColor: theme.palette.border.default,
      color: theme.palette.text.default,
      iconColor: theme.palette.text.subtle,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.default),
    },
    hover: {
      backgroundColor: theme.palette.background.secondaryHover,
      borderColor: theme.palette.border.default,
      color: theme.palette.text.default,
      iconColor: theme.palette.text.subtle,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.default),
    },
    focus: {
      backgroundColor: theme.palette.background.secondaryDefault,
      borderColor: theme.palette.border.focus,
      color: theme.palette.text.default,
      iconColor: theme.palette.text.subtle,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.focus),
      outline: `${theme.lelySpacing._2XS} solid ${theme.palette.common.black}`,
      outlineOffset: theme.lelySpacing._2XS,
    },
    active: {
      backgroundColor: theme.palette.background.secondaryActive,
      borderColor: theme.palette.border.default,
      color: theme.palette.text.default,
      iconColor: theme.palette.text.subtle,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.default),
    },
    disabled: {
      backgroundColor: theme.palette.background.secondaryDefault,
      borderColor: theme.palette.border.default,
      color: theme.palette.text.default,
      iconColor: theme.palette.text.subtle,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.default),
    },
  },
  tertiary: getTertiaryButtonStates(theme),
});

const getInvertedButtonStates = (
  theme: Theme,
): Record<ButtonVariant, ButtonStateStyles> => ({
  primary: {
    default: {
      backgroundColor: theme.palette.background.primaryInvertedDefault,
      borderColor: theme.palette.background.primaryInvertedDefault,
      color: theme.palette.text.light,
    },
    hover: {
      backgroundColor: theme.palette.background.primaryInvertedHover,
      borderColor: theme.palette.background.primaryInvertedHover,
      color: theme.palette.text.light,
    },
    focus: {
      backgroundColor: theme.palette.background.primaryInvertedDefault,
      borderColor: theme.palette.text.light,
      color: theme.palette.text.light,
      boxShadow: 'none',
      outline: `4px solid ${theme.palette.text.light}`,
      outlineOffset: theme.lelySpacing._2XS,
    },
    active: {
      backgroundColor: theme.palette.background.primaryInvertedActive,
      borderColor: theme.palette.background.primaryInvertedActive,
      color: theme.palette.text.light,
    },
    disabled: {
      backgroundColor: theme.palette.background.primaryInvertedDefault,
      borderColor: theme.palette.background.primaryInvertedDefault,
      color: theme.palette.text.light,
    },
  },
  secondary: {
    default: {
      backgroundColor: theme.palette.background.secondaryInvertedDefault,
      borderColor: theme.palette.border.light,
      color: theme.palette.text.light,
      iconColor: theme.palette.text.light,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.light),
    },
    hover: {
      backgroundColor: theme.palette.background.secondaryInvertedHover,
      borderColor: theme.palette.border.light,
      color: theme.palette.text.light,
      iconColor: theme.palette.text.light,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.light),
    },
    focus: {
      backgroundColor: theme.palette.background.secondaryInvertedDefault,
      borderColor: theme.palette.text.light,
      color: theme.palette.text.light,
      iconColor: theme.palette.text.light,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.light),
      outline: `${theme.lelySpacing._2XS} solid ${theme.palette.text.light}`,
      outlineOffset: theme.lelySpacing._2XS,
    },
    active: {
      backgroundColor: theme.palette.background.secondaryInvertedActive,
      borderColor: theme.palette.border.light,
      color: theme.palette.text.light,
      iconColor: theme.palette.text.light,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.light),
    },
    disabled: {
      backgroundColor: theme.palette.background.secondaryInvertedDefault,
      borderColor: theme.palette.border.light,
      color: theme.palette.text.light,
      iconColor: theme.palette.text.light,
      border: '0',
      boxShadow: getInsetBorder(theme.palette.border.light),
    },
  },
  tertiary: getTertiaryButtonStates(theme, true),
});

const getButtonVariantStateStyles = (
  states: ButtonStateStyles,
  theme: Theme,
) => ({
  backgroundColor: states.default.backgroundColor,
  border: states.default.border,
  borderColor: states.default.borderColor,
  boxShadow: states.default.boxShadow,
  color: states.default.color,

  '& .icon-svg': {
    color: states.default.iconColor ?? 'currentColor',
  },

  '&[data-preview-state="default"]': {
    backgroundColor: states.default.backgroundColor,
    border: states.default.border,
    borderColor: states.default.borderColor,
    boxShadow: states.default.boxShadow,
    color: states.default.color,

    '& .icon-svg': {
      color: states.default.iconColor ?? 'currentColor',
    },
  },

  '&:hover, &[data-preview-state="hover"]': {
    backgroundColor: states.hover.backgroundColor,
    border: states.hover.border,
    borderColor: states.hover.borderColor,
    color: states.hover.color,
    boxShadow: states.hover.boxShadow ?? 'none',

    '& .icon-svg': {
      color: states.hover.iconColor ?? 'currentColor',
    },
  },

  '&.Mui-focusVisible, &[data-preview-state="focus"]': {
    backgroundColor: states.focus.backgroundColor,
    border: states.focus.border,
    borderColor: states.focus.borderColor,
    color: states.focus.color,
    boxShadow:
      states.focus.boxShadow ?? `0 0 0 1px ${states.focus.borderColor}`,
    outline: states.focus.outline,
    outlineOffset: states.focus.outlineOffset,

    '& .icon-svg': {
      color: states.focus.iconColor ?? 'currentColor',
    },
  },

  '&:active, &[data-preview-state="active"]': {
    backgroundColor: states.active.backgroundColor,
    border: states.active.border,
    borderColor: states.active.borderColor,
    color: states.active.color,
    boxShadow: states.active.boxShadow ?? 'none',

    '& .icon-svg': {
      color: states.active.iconColor ?? 'currentColor',
    },
  },

  '&.Mui-disabled': {
    backgroundColor: states.default.backgroundColor,
    border: states.default.border,
    borderColor: states.default.borderColor,
    boxShadow: states.default.boxShadow,
    color: states.default.color,
    opacity: states.disabled.opacity ?? theme.palette.action.disabledOpacity,

    '& .icon-svg': {
      color: states.default.iconColor ?? 'currentColor',
    },
  },

  '&.MuiLoadingButton-loading': {
    color: 'transparent',
  },
});

const getButtonStateStyles = (
  states: ButtonStateStyles,
  invertedStates: ButtonStateStyles,
  theme: Theme,
) => ({
  ...getButtonVariantStateStyles(states, theme),

  '&.LelyButton-inverted, &[data-inverted="true"]': getButtonVariantStateStyles(
    invertedStates,
    theme,
  ),
});

const getIconOnlySize = (
  theme: Theme,
  iconSize: string,
  buttonPadding: string,
) =>
  `calc(${iconSize} + ${theme.lelySpacing._2XS} + ${theme.lelySpacing._2XS} + ${buttonPadding} + ${buttonPadding})`;

const getBaseIconOnlyStyles = (theme: Theme) => {
  const iconOnlySize = getIconOnlySize(
    theme,
    theme.lelySpacing.XL,
    theme.lelySpacing.MD,
  );

  return {
    boxSizing: 'border-box' as const,
    minWidth: iconOnlySize,
    width: iconOnlySize,
    minHeight: iconOnlySize,
    height: iconOnlySize,
    padding: theme.lelySpacing.MD,
    gap: 0,

    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      margin: 0,
    },
  };
};

const getIconOnlyStyles = (theme: Theme) => ({
  ...getBaseIconOnlyStyles(theme),
  borderRadius: '50%',
});

const getSquareIconOnlyStyles = (theme: Theme) => ({
  ...getBaseIconOnlyStyles(theme),
  borderRadius: theme.lelyBorderRadius.SM,
});

const getDenseStyles = (theme: Theme) => ({
  minHeight: theme.lelySpacing._2XL,
  padding: `0 ${theme.lelySpacing.XS}`,
  gap: theme.lelySpacing.XS,

  '& .icon-svg': {
    width: theme.lelySpacing.XL,
    height: theme.lelySpacing.XL,
  },

  '& .MuiSvgIcon-root': {
    width: theme.lelySpacing.XL,
    height: theme.lelySpacing.XL,
  },

  '&.LelyButton-iconOnly, &[data-icon-only="true"], &.LelyButton-squareIconOnly, &[data-square-icon-only="true"]':
    {
      minWidth: getIconOnlySize(
        theme,
        theme.lelySpacing.LG,
        theme.lelySpacing._2XS,
      ),
      width: getIconOnlySize(
        theme,
        theme.lelySpacing.LG,
        theme.lelySpacing._2XS,
      ),
      minHeight: getIconOnlySize(
        theme,
        theme.lelySpacing.LG,
        theme.lelySpacing._2XS,
      ),
      height: getIconOnlySize(
        theme,
        theme.lelySpacing.LG,
        theme.lelySpacing._2XS,
      ),
      padding: theme.lelySpacing._2XS,
    },
});

const getButtonComponent = (theme: Theme): Components['MuiButton'] => {
  const buttonStates = getButtonStates(theme);
  const invertedButtonStates = getInvertedButtonStates(theme);

  return {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        ...theme.typography.base,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: theme.lelyFontSize.SM,
        lineHeight: theme.lelyLineHeight.BASE,
        textTransform: 'none',
        padding: `${theme.lelySpacing.MD} ${theme.lelySpacing.MD}`,
        borderRadius: theme.lelyBorderRadius.SM,
        border: 0,
        boxShadow: 'none',
        minWidth: 0,
        minHeight: theme.lelySpacing._8XL,

        '& .MuiButton-startIcon': {
          marginLeft: 0,
          marginRight: 0,
        },

        '& .MuiButton-endIcon': {
          marginLeft: 0,
          marginRight: 0,
        },

        '& .icon-svg': {
          color: 'currentColor',
          width: theme.lelySpacing._2XL,
          height: theme.lelySpacing._2XL,
        },

        '& .MuiSvgIcon-root': {
          color: 'currentColor',
          width: theme.lelySpacing._2XL,
          height: theme.lelySpacing._2XL,
        },

        '&.LelyButton-iconOnly, &[data-icon-only="true"]':
          getIconOnlyStyles(theme),
        '&.LelyButton-squareIconOnly, &[data-square-icon-only="true"]':
          getSquareIconOnlyStyles(theme),
        '&.LelyButton-dense, &[data-dense="true"]': getDenseStyles(theme),
        '&.LelyButton-fullWidth, &[data-full-width="true"]': {
          width: '100%',
        },
      },
    },

    variants: [
      {
        props: { variant: 'primary' },
        style: () => ({
          display: 'inline-flex',
          gap: theme.lelySpacing.SM,
          cursor: 'pointer',
          ...getButtonStateStyles(
            buttonStates.primary,
            invertedButtonStates.primary,
            theme,
          ),
        }),
      },
      {
        props: { variant: 'secondary' },
        style: () => ({
          display: 'inline-flex',
          gap: theme.lelySpacing.SM,
          cursor: 'pointer',
          ...getButtonStateStyles(
            buttonStates.secondary,
            invertedButtonStates.secondary,
            theme,
          ),
        }),
      },
      {
        props: { variant: 'tertiary' },
        style: () => ({
          display: 'inline-flex',
          gap: theme.lelySpacing.SM,
          cursor: 'pointer',
          ...getButtonStateStyles(
            buttonStates.tertiary,
            invertedButtonStates.tertiary,
            theme,
          ),
        }),
      },
    ],
  };
};

export const getButtonComponents = (
  theme: Theme,
): Pick<Components, 'MuiButton' | 'MuiLoadingButton'> => {
  const buttonComponent = getButtonComponent(theme);

  return {
    MuiButton: buttonComponent,
    MuiLoadingButton: buttonComponent as Components['MuiLoadingButton'],
  };
};
